import { jwtDecode } from 'jwt-decode';

// The refresh-token cookie is shared across every tab on this domain, but sessionStorage is
// per-tab. Tagging a tab with the user id it expects to be lets it detect when a *different*
// tab (via login, switchTo, or admin impersonation) has overwritten that shared cookie.
const EXPECTED_USER_ID_KEY = 'hubert_expected_user_id';

const decodeUserId = (accessToken: string): string | null => {
  try {
    const { userId } = jwtDecode<{ userId?: string }>(accessToken);
    return userId ?? null;
  } catch {
    return null;
  }
};

export const setExpectedUserId = (accessToken: string) => {
  const userId = decodeUserId(accessToken);
  if (userId) {
    sessionStorage.setItem(EXPECTED_USER_ID_KEY, userId);
  }
};

export const getExpectedUserId = () => sessionStorage.getItem(EXPECTED_USER_ID_KEY);

export const clearExpectedUserId = () => {
  sessionStorage.removeItem(EXPECTED_USER_ID_KEY);
};

export const matchesExpectedUserId = (accessToken: string): boolean => {
  const expected = getExpectedUserId();
  if (!expected) {
    return true;
  }
  return decodeUserId(accessToken) === expected;
};

// Set right before a forced end-of-session redirect to /signin. A hard navigation there
// remounts the app, and the mount-time bootstrap would otherwise happily re-authenticate off
// whatever the shared refresh cookie currently holds - which, in the identity-mismatch case,
// is still a *valid* token, just for the wrong person, so /signin would immediately auto-redirect
// back into the app under that identity. This flag tells that one bootstrap to skip the cookie
// entirely and show a real sign-in form instead.
const SKIP_AUTO_LOGIN_KEY = 'hubert_skip_auto_login';

export const setSkipAutoLogin = () => {
  sessionStorage.setItem(SKIP_AUTO_LOGIN_KEY, '1');
};

/** Reads and clears the flag in one step, so it only ever suppresses a single bootstrap. */
export const consumeSkipAutoLogin = (): boolean => {
  const shouldSkip = sessionStorage.getItem(SKIP_AUTO_LOGIN_KEY) === '1';
  sessionStorage.removeItem(SKIP_AUTO_LOGIN_KEY);
  return shouldSkip;
};

const SIGN_OUT_REASON_KEY = 'hubert_signout_reason';

export const setSignOutReason = (reason: string) => {
  sessionStorage.setItem(SIGN_OUT_REASON_KEY, reason);
};

export const consumeSignOutReason = (): string | null => {
  const reason = sessionStorage.getItem(SIGN_OUT_REASON_KEY);
  sessionStorage.removeItem(SIGN_OUT_REASON_KEY);
  return reason;
};
